import { AnchorButton, Button, ViewFieldset } from '@payaca/ambrosia';
import Lightbox from '@payaca/components/lightbox/Lightbox';

import { CustomFieldHelperType, CustomFieldType } from '@payaca/custom-fields';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useState } from 'react';
import { useIsoDateFormatter } from './dateFormatContext';
import { buildUploadTiles, isHttpUrl } from './uploadLightbox';

export type TProps<T extends CustomFieldType> = {
  type: T;
  definition: CustomFieldHelperType[T]['definition'];
  value?: CustomFieldHelperType[T]['value'] | null;
  customFormatter?: (
    value: CustomFieldHelperType[T]['value']
  ) => React.ReactNode;
};

const CustomFieldReadonly = <T extends CustomFieldType>(props: TProps<T>) => {
  const { definition, value, type } = props;
  const formatIsoDate = useIsoDateFormatter();

  let customFormatter: any = props.customFormatter || undefined;
  if (type === 'email') {
    customFormatter = (value: CustomFieldHelperType['email']['value']) => {
      return <a href={`mailto:${value}`}>{value}</a>;
    };
  }
  if (definition.type === 'url') {
    customFormatter = (value: CustomFieldHelperType['url']['value']) => {
      if (!value) return null;
      return isHttpUrl(value.url) ? (
        <a target="_blank" rel="noreferrer" href={value.url}>
          {value.title || value.url}
        </a>
      ) : (
        <span>{value.title || value.url}</span>
      );
    };
  }
  if (definition.type === 'date') {
    customFormatter = (value: CustomFieldHelperType['date']['value']) =>
      formatIsoDate(value);
  }
  if (definition.type === 'textarea') {
    customFormatter = (value: CustomFieldHelperType['textarea']['value']) => {
      if (!value) return null;
      return <div className="whitespace-pre-wrap">{value}</div>;
    };
  }
  if (definition.type === 'upload') {
    customFormatter = (value: CustomFieldHelperType['upload']['value']) => {
      if (!value || value.length === 0) return null;
      return <UploadsList value={value} />;
    };
  }

  return (
    <ViewFieldset.Field
      label={`${definition.label}:`}
      description={definition.description}
    >
      {definition.formatValue(value, customFormatter) || '-'}
    </ViewFieldset.Field>
  );
};

export default CustomFieldReadonly;

const FILE_TILE_CLASSNAME = 'flex items-center gap-2 max-w-52';

const UploadsList: FC<{ value: CustomFieldHelperType['upload']['value'] }> = ({
  value,
}) => {
  const items = buildUploadTiles(value);
  const previewable = items.filter((item) => item.detail !== null);

  const [openSlideIndex, setOpenSlideIndex] = useState<number | null>(null);

  const hasNextSlide =
    openSlideIndex !== null && openSlideIndex + 1 < previewable.length;
  const hasPrevSlide = openSlideIndex !== null && openSlideIndex - 1 >= 0;

  const openSlideDetail =
    openSlideIndex !== null ? previewable[openSlideIndex]?.detail : undefined;

  return (
    <>
      <ul className="list-none p-0 m-0 flex gap-2 flex-wrap items-start">
        {items.map(
          ({ upload, category, isSafeUrl, detail, slideIndex }, index) => {
            const key = `${index}-${upload.url}`;

            if (category === 'image' && detail) {
              return (
                <li key={key}>
                  <button
                    className="p-0 border-0 bg-transparent cursor-pointer"
                    onClick={() => setOpenSlideIndex(slideIndex)}
                  >
                    <img
                      src={upload.thumbnailUrl ?? upload.url}
                      alt={upload.name}
                      className="block size-20 object-cover rounded-md"
                    />
                  </button>
                </li>
              );
            }

            // A file whose URL isn't a safe http(s) link can't be opened or
            // downloaded, so surface just its name.
            if (!isSafeUrl) {
              return <li key={key}>{upload.name}</li>;
            }

            const icon = category === 'video' ? 'video-file' : 'file-04';
            const tileContent = (
              <>
                <UntitledIcon name={icon} className="w-5 h-5 shrink-0" />
                <span className="truncate min-w-0" title={upload.name}>
                  {upload.name}
                </span>
              </>
            );

            return (
              <li key={key}>
                {detail ? (
                  <Button
                    variant="secondary"
                    className={FILE_TILE_CLASSNAME}
                    onClick={() => setOpenSlideIndex(slideIndex)}
                  >
                    {tileContent}
                  </Button>
                ) : (
                  <AnchorButton
                    variant="secondary"
                    className={FILE_TILE_CLASSNAME}
                    href={upload.url}
                    openInNewTab
                    download
                  >
                    {tileContent}
                  </AnchorButton>
                )}
              </li>
            );
          }
        )}
      </ul>

      <Lightbox
        isOpen={openSlideIndex !== null}
        onClose={() => setOpenSlideIndex(null)}
        onNext={
          hasNextSlide
            ? () => setOpenSlideIndex((prev) => (prev ?? 0) + 1)
            : undefined
        }
        onPrev={
          hasPrevSlide
            ? () => setOpenSlideIndex((prev) => (prev ?? 0) - 1)
            : undefined
        }
        lightboxDetail={openSlideDetail ?? { type: 'loading' }}
      />
    </>
  );
};
